import ProductTemplateColumnsDropdown
    from "components/basic/dropdowns/product-template-columns-dropdown/ProductTemplateColumnsDropdown";
import {
    IProductTemplateDropdownChangeParams,
    ProductTemplateDropdown
} from "components/basic/dropdowns/product-template/ProductTemplateDropdown";
import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import {ICommonComponentEditorProps} from "components/ComponentType";
import {IProductTemplateTableProps} from "components/dashboard/product-template-table/ProductTemplateTable";
import {LoadingConfig} from "components/dashboard/product-template-table/utils/interfaces";
import {InputNumber} from "primereact/inputnumber";
import {InputSwitch} from "primereact/inputswitch";
import {InputText} from "primereact/inputtext";
import React, {FunctionComponent, useEffect, useState} from "react";

interface IProductTemplateTableEditorProps {
    id?: number;
    safeName: string;
    transposed?: boolean;
    showTitle?: boolean;
    updateFrequency?: number;
    hiddenColumns?: Array<string>;
    onlyShownColumns?: Array<string>;
    itemsPerPage?: number;
    overrideLoadingConfig?: LoadingConfig;
    filter?: string;
    hideDates?: boolean;
    showFilter?: boolean;
    allowAddEntry?: boolean;
    allowRowEdit?: boolean;
    showTotals?: boolean;
}

const defaultProductTemplateEditorConfig: IProductTemplateTableEditorProps = {
    id: undefined,
    safeName: "",
    transposed: false,
    showTitle: true,
    updateFrequency: undefined,
    hiddenColumns: [],
    onlyShownColumns: [],
    itemsPerPage: undefined,
    overrideLoadingConfig: undefined,
    filter: undefined,
    hideDates: false,
    showFilter: false,
    allowAddEntry: true,
    allowRowEdit: true,
    showTotals: false
};


const ProductTemplateTableEditor = ({config, onConfigChange}: ICommonComponentEditorProps) => {
    let conf: IProductTemplateTableEditorProps = config ?? defaultProductTemplateEditorConfig;
    if (Object.keys(conf).length === 0) {
        conf = defaultProductTemplateEditorConfig;
    }

    const [currentConfig, setCurrentConfig] = useState(conf);
    const [selectedTemplate, setSelectedTemplate] = useState<IProductTemplateDropdownChangeParams>({
        value: 0,
        refName: ""
    });
    const [overrideConfig, setOverrideConfig] = useState(!!conf.overrideLoadingConfig);


    useEffect(() => {
        onConfigChange(currentConfig);
    }, [currentConfig]);

    const setConfigPropertyState = (property: keyof IProductTemplateTableProps, value: any) => {
        setCurrentConfig((prevState) => ({...prevState, [property]: value}));
    };

    const handleConfigChange = (e: any): void => {
        setConfigPropertyState(e.target.id as keyof IProductTemplateTableProps, e.target.value);
    };

    const safeNameSelectionHandler = (e: any) => {

        setConfigPropertyState("safeName", e.refName);
        setCurrentConfig((prevState) => ({...prevState, id: e.value}));
        setSelectedTemplate(e);
    };

    const hiddenShownColumnsDropdownHandler = (e: any): void => {
        setConfigPropertyState(e.target.id, e.target.value);
    };

    const loadingConfigHandler = (e: any) => {
        setCurrentConfig((prevState) => ({
            ...prevState,
            overrideLoadingConfig: {
                ...prevState.overrideLoadingConfig,
                loadingConfig: {
                    ...prevState.overrideLoadingConfig?.loadingConfig,
                    [e.target.id]: !isNaN(e.target.value) ? e.target.value : e.target.value.toString().toUpperCase()
                }
            },
        }));
    };

    const overrideConfigHandler = () => {
        if (overrideConfig) {
            setOverrideConfig(false);
            const configState = {...currentConfig};
            delete configState.overrideLoadingConfig;
            setCurrentConfig(configState);
        } else {
            setOverrideConfig(true);
        }
    };

    useEffect(() => {
        setSelectedTemplate({value: currentConfig.id!, refName: currentConfig.safeName});
    }, []);

    return (
        <div className="grid p-fluid">
            <div className="col-12 mb-3 flex flex-column">
                <label htmlFor="safeName">Safe Name</label>
                <ProductTemplateDropdown id="safeName" value={currentConfig.id} onChange={safeNameSelectionHandler}/>
            </div>
            <div className="col-8 gap-2 flex flex-column justify-content-center">
                <label htmlFor="filter">Filter</label>
                <InputText id="filter" value={currentConfig.filter} onChange={handleConfigChange}/>
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="itemsPerPage">Items Per Page</label>
                <InputNumber
                    id="itemsPerPage"
                    value={currentConfig?.itemsPerPage}
                    onValueChange={handleConfigChange}
                />
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="updateFrequency">Update Frequency</label>
                <div className="p-inputgroup">
                    <InputNumber
                        id="updateFrequency"
                        value={currentConfig?.updateFrequency as number}
                        onValueChange={handleConfigChange}
                    />
                    <span className="p-inputgroup-addon">s</span>
                </div>
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="transposed">Transpose</label>
                <InputSwitch id="transposed" checked={currentConfig.transposed} onChange={handleConfigChange}/>
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="showTitle">Show Title</label>
                <InputSwitch id="showTitle" checked={currentConfig.showTitle} onChange={handleConfigChange}/>
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="hideDates">Hide Dates</label>
                <InputSwitch id="hideDates" checked={currentConfig.hideDates} onChange={handleConfigChange}/>
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="showFilter">Show filter</label>
                <InputSwitch id="showFilter" checked={currentConfig.showFilter} onChange={handleConfigChange}/>
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="allowAddEntry">Allow Add Entry</label>
                <InputSwitch id="allowAddEntry" checked={currentConfig.allowAddEntry} onChange={handleConfigChange}/>
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="allowRowEdit">Allow Row Edit</label>
                <InputSwitch id="allowRowEdit" checked={currentConfig.allowRowEdit} onChange={handleConfigChange}/>
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="showTotals">Show Totals</label>
                <InputSwitch id="showTotals" checked={currentConfig.showTotals} onChange={handleConfigChange}/>
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="overrideConfig">Override Config</label>
                <InputSwitch id="overrideConfig" checked={overrideConfig} onChange={overrideConfigHandler}/>
            </div>
            {selectedTemplate.refName && <>
                <div className="col-3 gap-2 flex flex-column justify-content-center align-items-center">
                    <label htmlFor="hiddenColumns">Hidden Columns</label>
                    <ProductTemplateColumnsDropdown
                        id="hiddenColumns"
                        value={Array.isArray(currentConfig.hiddenColumns) ? currentConfig.hiddenColumns : []}
                        className="w-full"
                        onChange={hiddenShownColumnsDropdownHandler}
                        selectedTemplate={selectedTemplate}
                    />
                </div>
                <div className="col-3 gap-2 flex flex-column justify-content-center align-items-center">
                    <label htmlFor="onlyShownColumns">Only Show Columns</label>
                    <ProductTemplateColumnsDropdown
                        id="onlyShownColumns"
                        value={Array.isArray(currentConfig.onlyShownColumns) ? currentConfig.onlyShownColumns : []}
                        className="w-full"
                        onChange={hiddenShownColumnsDropdownHandler}
                        selectedTemplate={selectedTemplate}
                        disabled={currentConfig.hiddenColumns && currentConfig.hiddenColumns?.length > 0}
                    />
                </div>
            </>
            }
            {overrideConfig && (
                <>
                    <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
                        <label htmlFor="period">Period</label>
                        <div className="p-inputgroup">
                            <InputNumber
                                id="period"
                                value={currentConfig.overrideLoadingConfig?.loadingConfig?.period}
                                onValueChange={loadingConfigHandler}
                            />
                            <TimeUnitDropdown
                                id="periodUnit"
                                className="p-inputgroup-addon p-0"
                                value={currentConfig.overrideLoadingConfig?.loadingConfig?.periodUnit}
                                onChange={loadingConfigHandler}
                            />
                        </div>
                    </div>
                    <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
                        <label htmlFor="offset">Offset</label>
                        <div className="p-inputgroup">
                            <InputNumber
                                id="offset"
                                value={currentConfig.overrideLoadingConfig?.loadingConfig?.offset}
                                onValueChange={loadingConfigHandler}
                            />
                            <TimeUnitDropdown
                                id="offsetUnit"
                                className="p-inputgroup-addon p-0"
                                value={currentConfig.overrideLoadingConfig?.loadingConfig?.offsetUnit!}
                                onChange={loadingConfigHandler}
                            />
                        </div>
                    </div>
                    <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                        <label htmlFor="defaultConfigUnit">Default Config unit</label>
                        <TimeUnitDropdown
                            id="defaultConfigUnit"
                            value={currentConfig.overrideLoadingConfig?.loadingConfig?.defaultConfigUnit}
                            onChange={loadingConfigHandler}/>
                    </div>
                    <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                        <label htmlFor="truncateStartUnit">Truncate Start Unit</label>
                        <TimeUnitDropdown
                            id="truncateStartUnit"
                            value={currentConfig?.overrideLoadingConfig?.loadingConfig?.truncateStartUnit}
                            onChange={loadingConfigHandler}/>
                    </div>
                </>
            )}
        </div>
    );
};

export default ProductTemplateTableEditor as FunctionComponent;